import { useCallback, useContext, useState } from 'react';

import { Text } from '@chakra-ui/react';
import { TbCarCrash } from 'react-icons/tb';

import { useOnEmbeddedInsuranceNotificationSubscription } from '../../gql/generated/graphql';
import type { EmbeddedInsuranceNotification } from '../../gql/generated/graphql';

import NotificationToast from './components/NotificationToast';

import { InteractedContext } from '../../libs/contextLib';

const EmbeddedInsuranceHandler = () => {
  const hasInteracted = useContext(InteractedContext);

  const [notifications, setNotifications] = useState<
    Map<EmbeddedInsuranceNotification['deal_id'], EmbeddedInsuranceNotification>
  >(new Map());

  useOnEmbeddedInsuranceNotificationSubscription({
    onData: ({ data }) => {
      const notification = data.data?.onEmbeddedInsuranceNotification;
      if (!notification) {
        return;
      }

      // Always replace with the latest notification for the `deal_id`.
      setNotifications((prev) => {
        const newMap = new Map(prev);
        newMap.set(notification.deal_id, notification);
        return newMap;
      });

      if (hasInteracted) {
        new Audio('/sounds/sms_notification.mp3').play();
      }
    },
  });

  const handleCloseNotification = useCallback(
    (dealId: EmbeddedInsuranceNotification['deal_id']) => {
      setNotifications((prev) => {
        const newMap = new Map(prev);
        newMap.delete(dealId);
        return newMap;
      });
    },
    [],
  );

  return (
    <>
      {Array.from(notifications.values()).map((d) => (
        <NotificationToast
          key={`${d.deal_id}`}
          dealId={d.deal_id}
          slideFadeIn
          title={`${d.first_name} ${d.last_name} - Deal ${d.deal_id}`}
          titleIcon={TbCarCrash}
          titleIconColor={d.success ? 'green' : 'red'}
          subTitle={
            d.success
              ? 'Insurance Document Received via Embedded Insurance'
              : 'Embedded Insurance Upload Failed'
          }
          details={
            d.success ? (
              <Text>{d.filename || 'Document'} Uploaded to the Media Center</Text>
            ) : (
              <Text>
                No document was received from Embedded Insurance flow. Follow-up may be needed.
              </Text>
            )
          }
          onClose={() => handleCloseNotification(d.deal_id)}
        />
      ))}
    </>
  );
};

export default EmbeddedInsuranceHandler;
